import { motion } from 'motion/react';
import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { ANIMATION_CONFIG } from '../../../constants/animationConfig';
import { COLORS } from '../../../constants';
import { isSafariBrowser } from '../../../utils/isSafariBrowser';

interface MotionMainHeaderBackgroundProps extends PropsWithChildren {
  className?: string;
}

export const MotionMainHeaderBackground = ({
  children,
  className,
}: MotionMainHeaderBackgroundProps) => {
  const isSafari = isSafariBrowser();
  if (isSafari) {
    return (
      <StaticBackground className={className}>
        {children}
      </StaticBackground>
    );
  }
  return (
    <MotionMainHeaderBackgroundWrapper className={className}>
      {children}
    </MotionMainHeaderBackgroundWrapper>
  );
};

const StaticBackground = styled.div`
  background: ${COLORS.HEADER_HOME_BACKGROUND};
  overflow: hidden;
`;

const MotionMainHeaderBackgroundWrapper = styled(motion.div)`
  @property --angle {
    syntax: '<angle>';
    initial-value: 180deg;
    inherits: false;
  }
  @property --stop1 {
    syntax: '<percentage>';
    initial-value: 10.17%;
    inherits: false;
  }
  @property --stop2 {
    syntax: '<percentage>';
    initial-value: 16.05%;
    inherits: false;
  }
  @property --stop3 {
    syntax: '<percentage>';
    initial-value: 101.92%;
    inherits: false;
  }
  @property --colorPrimary {
    syntax: '<color>';
    initial-value: #2f234c;
    inherits: false;
  }
  @property --colorSecondary {
    syntax: '<color>';
    initial-value: #2f234c;
    inherits: false;
  }

  overflow: hidden;
  --angle: 180deg;
  --stop1: 10.17%;
  --stop2: 16.05%;
  --stop3: 101.92%;
  background: linear-gradient(
    var(--angle),
    var(--colorSecondary) var(--stop1),
    var(--colorPrimary) var(--stop2),
    var(--colorSecondary) var(--stop3)
  );
  animation: floating-gradient-main infinite;
  animation-duration: ${ANIMATION_CONFIG.keyFrames.mainHeader.duration}s;
  animation-delay: ${ANIMATION_CONFIG.keyFrames.mainHeader.delay}s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);

  @keyframes floating-gradient-main {
    0%   {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --stop1: 10.17%;
      --stop2: 16.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    8%   {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --stop1: 10.17%;
      --stop2: 56.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    17%  {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --angle: 180deg;

      --stop1: 10.17%;
      --stop2: 56.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    25%  {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 180deg;

      --stop1: 10.17%;
      --stop2: 56.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    33%  {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 120deg;

      --stop1: 10.17%;
      --stop2: 86.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    42%  {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --stop1: 10.17%;
      --stop2: 26.05%;
      --stop3: 41.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    50%  {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --stop1: 10.17%;
      --stop2: 26.05%;
      --stop3: 41.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    58%  {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 120deg;

      --stop1: 10.17%;
      --stop2: 26.05%;
      --stop3: 41.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    67%  {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 90deg;

      --stop1: 10.17%;
      --stop2: 26.05%;
      --stop3: 41.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    75%  {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --angle: 90deg;

      --stop1: 80.17%;
      --stop2: 96.05%;
      --stop3: 161.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    83%  {
      --colorPrimary: #a32933;
      --colorSecondary: #2f234c;

      --angle: 90deg;

      --stop1: 80.17%;
      --stop2: 96.05%;
      --stop3: 161.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    92%  {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 90deg;

      --stop1: 80.17%;
      --stop2: 96.05%;
      --stop3: 161.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
    100% {
      --colorPrimary: #2f234c;
      --colorSecondary: #2f234c;

      --angle: 90deg;

      --stop1: 80.17%;
      --stop2: 96.05%;
      --stop3: 101.92%;

      background: linear-gradient(
        var(--angle),
        var(--colorSecondary) var(--stop1),
        var(--colorPrimary) var(--stop2),
        var(--colorSecondary) var(--stop3)
      );
    }
  }
`;
